"use client";

import { m as motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

/**
 * Visual animato del framework MCE: cicla Mappa -> Costruisci -> Evolvi.
 * Lettera grande in ciano + una mini-scena che si muove per ogni fase,
 * con dissolvenza tra una fase e l'altra, in loop continuo.
 */

const S = "hsl(var(--primary))";

const PHASES = [
  { letter: "M", it: "Mappa", en: "Map" },
  { letter: "C", it: "Costruisci", en: "Build" },
  { letter: "E", it: "Evolvi", en: "Evolve" },
];

/** Mappa: nodi che compaiono e si collegano. */
function MapScene() {
  const nodes: Array<[number, number]> = [
    [14, 44],
    [40, 18],
    [62, 50],
    [86, 24],
    [50, 78],
  ];
  const edges: Array<[number, number]> = [
    [0, 1],
    [1, 2],
    [2, 3],
    [2, 4],
    [0, 4],
  ];
  return (
    <svg viewBox="0 0 100 100" className="h-full w-full" aria-hidden>
      <g stroke={S} strokeWidth={1.4} strokeLinecap="round" fill="none">
        {edges.map(([a, b], i) => (
          <motion.line
            key={i}
            x1={nodes[a][0]}
            y1={nodes[a][1]}
            x2={nodes[b][0]}
            y2={nodes[b][1]}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.55 }}
            transition={{ duration: 0.7, delay: 0.25 + i * 0.12, ease: "easeInOut" }}
          />
        ))}
      </g>
      <g fill={S}>
        {nodes.map(([x, y], i) => (
          <motion.circle
            key={i}
            cx={x}
            cy={y}
            r={3.4}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.3, 1], opacity: 1 }}
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
            transition={{ duration: 0.5, delay: i * 0.12 }}
          />
        ))}
      </g>
    </svg>
  );
}

/** Costruisci: blocchi che salgono e si impilano. */
function BuildScene() {
  const blocks = [
    { x: 26, y: 60, w: 48, h: 12 },
    { x: 32, y: 44, w: 36, h: 12 },
    { x: 38, y: 28, w: 24, h: 12 },
  ];
  return (
    <svg viewBox="0 0 100 100" className="h-full w-full" aria-hidden>
      <line x1={18} y1={78} x2={82} y2={78} stroke={S} strokeWidth={1.4} strokeLinecap="round" opacity={0.4} />
      {blocks.map((b, i) => (
        <motion.rect
          key={i}
          x={b.x}
          width={b.w}
          height={b.h}
          rx={2.5}
          y={b.y}
          stroke={S}
          strokeWidth={1.5}
          fill={S}
          fillOpacity={0.12}
          initial={{ y: 84, opacity: 0 }}
          animate={{ y: b.y, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 + i * 0.22, ease: [0.22, 0.7, 0.2, 1] }}
        />
      ))}
    </svg>
  );
}

/** Evolvi: una linea che cresce verso l'alto con freccia. */
function EvolveScene() {
  return (
    <svg viewBox="0 0 100 100" className="h-full w-full" aria-hidden>
      <g stroke={S} fill="none" strokeLinecap="round" strokeLinejoin="round">
        <line x1={16} y1={82} x2={88} y2={82} strokeWidth={1.4} opacity={0.4} />
        <motion.polyline
          points="20,72 40,58 56,64 76,30"
          strokeWidth={2.2}
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1, ease: "easeInOut" }}
        />
        <motion.path
          d="M68 30 L76 30 L76 38"
          strokeWidth={2.2}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.3 }}
        />
      </g>
      {[
        [40, 58],
        [56, 64],
        [76, 30],
      ].map(([x, y], i) => (
        <motion.circle
          key={i}
          cx={x}
          cy={y}
          r={2.8}
          fill={S}
          style={{ transformBox: "fill-box", transformOrigin: "center" }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.4 + i * 0.25, duration: 0.3 }}
        />
      ))}
    </svg>
  );
}

const SCENES = [MapScene, BuildScene, EvolveScene];

export function MetodoFrameworkVisual({ isItalian }: { isItalian: boolean }) {
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => setI((p) => (p + 1) % PHASES.length), 3000);
    return () => window.clearInterval(id);
  }, [reduce]);

  const Scene = SCENES[i];
  const phase = PHASES[i];

  return (
    <div className="relative mx-auto aspect-square w-full max-w-xs md:max-w-sm">
      <div className="pointer-events-none absolute inset-0 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative h-full w-full overflow-hidden rounded-3xl border border-[hsl(var(--border))] bg-surface/40">
        <div className="absolute inset-0 pattern-dots opacity-40" />
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.03 }}
            transition={{ duration: 0.55, ease: "easeInOut" }}
            className="absolute inset-0 flex flex-col items-center justify-center"
          >
            <div className="relative flex items-center justify-center">
              <span
                className="font-display font-bold leading-none opacity-[0.13] text-[12rem]"
                style={{ color: S }}
              >
                {phase.letter}
              </span>
              <div className="absolute h-28 w-28">
                <Scene />
              </div>
            </div>
            <span className="mt-6 font-mono text-sm uppercase tracking-[0.34em] text-primary">
              {isItalian ? phase.it : phase.en}
            </span>
          </motion.div>
        </AnimatePresence>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-2">
          {PHASES.map((_, n) => (
            <span
              key={n}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: n === i ? 18 : 6,
                background: n === i ? S : "hsl(var(--border))",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
